import { computed, inject, Injectable, signal } from '@angular/core';
import { CustomRouteService } from 'src/app/pages/custom-route/services/custom-route.service';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isBetween from 'dayjs/plugin/isBetween';
import { RestService, SessionStorageService, ToolsService, TranslationService } from 'src/app/services';
import { PlannerDayStateService } from '../../../../../services/planner-day-state.service';
import { CustomRoute, DailyRoute, Point } from 'src/app/pages/custom-route/interfaces/custom-route.interface';
import { StartEndPointUpdate } from '../../action-buttons/components/edit-destination/interfaces';
import { EditedCustomRouteDataSyncBody, EditedCustomRouteDataSyncResponse, EditedCustomRouteDataSyncSummary } from '../interfaces/edit-route.interface';
import { catchError, finalize, map, Observable, Subscription, throwError, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { CustomRouteResponseCompleted } from 'src/app/pages/custom-route/interfaces/custom-route.response';
import { customRouteAdapter } from 'src/app/pages/custom-route/adapters/custom-route.adapter';
import { getRouteStartHour } from 'src/app/pages/custom-route/helpers/route-start-hour';
import { normalizeUtcDatesInDailyRoutes } from 'src/app/pages/custom-route/helpers/normalize-utc-dates';

dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);

@Injectable({
  providedIn: 'root'
})
export class EditRouteService {
  // Service injections
  private _restService = inject(RestService);
  private _toolsService = inject(ToolsService);
  private _http = inject(HttpClient);
  private _translationService = inject(TranslationService);
  private _customRouteService = inject(CustomRouteService);
  private _sessionStorageService = inject(SessionStorageService);
  private _plannerDayStateService = inject(PlannerDayStateService);

  // State signals
  private _editedCustomRouteData = signal<CustomRoute | null>(null);
  public editedCustomRouteData = this._editedCustomRouteData.asReadonly();
  
  private _isLoadingEditedCustomRouteData = signal<boolean>(false);
  public isLoadingEditedCustomRouteData = this._isLoadingEditedCustomRouteData.asReadonly();
  
  private _errorEditedCustomRouteData = signal<string | null>(null);
  public errorEditedCustomRouteData = this._errorEditedCustomRouteData.asReadonly();
  
  private _summaryEditedCustomRouteData = signal<EditedCustomRouteDataSyncSummary | null>(null);
  public summaryEditedCustomRouteData = this._summaryEditedCustomRouteData.asReadonly();
  
  private _warningMessageEditedCustomRouteData = signal<string | null>(null);
  public warningMessageEditedCustomRouteData = this._warningMessageEditedCustomRouteData.asReadonly();

  private _isLoadingResourcesByIdOnEmptySpace = signal<number>(null);
  public isLoadingResourcesByIdOnEmptySpace = this._isLoadingResourcesByIdOnEmptySpace.asReadonly();

  private _isLoadingResourcesOnExistingItem = signal<boolean>(false);
  public isLoadingResourcesOnExistingItem = this._isLoadingResourcesOnExistingItem.asReadonly();
  
  private _isLoadingRestoreEditedCustomRouteData = signal<boolean>(false);
  public isLoadingRestoreEditedCustomRouteData = this._isLoadingRestoreEditedCustomRouteData.asReadonly();

  private _editActionCounter = signal<number>(0);
  public editActionCounter = this._editActionCounter.asReadonly();
  
  // Setters
  public setEditedCustomRouteData(customRoute: CustomRoute): void {
    // El backend puede devolver fechas naive (sin "Z"); sin normalizar, la
    // matemática UTC del timeline las interpreta en hora local y el drag&drop
    // calcula horas desplazadas respecto al grid.
    if (!customRoute?.dailyRoutes) {
      this._editedCustomRouteData.set(customRoute);
      return;
    }

    this._editedCustomRouteData.set({
      ...customRoute,
      dailyRoutes: normalizeUtcDatesInDailyRoutes(customRoute.dailyRoutes),
    });
  }
  
  public setEditActionCounter(editActionCount: number): void {
    this._editActionCounter.set(editActionCount);
    this._sessionStorageService.setItem('editActionCounter', editActionCount);
  }

  public setIsLoadingResourcesByIdOnEmptySpace(isLoadingId: number): void {
    this._isLoadingResourcesByIdOnEmptySpace.set(isLoadingId);
  }

  public setIsLoadingResourcesOnExistingItem(isLoading: boolean): void {
    this._isLoadingResourcesOnExistingItem.set(isLoading);
  }
  
  // Service signal states
  private _isTechMode = this._toolsService.isTechMode;
  
  // Computed signals
  /**
   * Hour (0-23) where the timeline grid starts for the current route
   * (HRS-2640). Combines the declared start time (registration form snapshot
   * or recommended-route template) with the earliest first-point hour across
   * days; falls back to START_TIME_HOUR (10). See helpers/route-start-hour.ts.
   */
  public routeStartHour = computed(() => {
    const route = this._editedCustomRouteData() ?? this._customRouteService.originalCustomRouteData();
    const declared = this._customRouteService.registerUserBeforeFormValues()?.startTime
      ?? route?.recommended_route?.start_time
      ?? null;
    return getRouteStartHour(route, declared);
  });

  public bodySyncEditedCustomRouteData = computed(() => {
    const editedData = this._editedCustomRouteData();
    const token = this._customRouteService.token();
    const clientId = this._restService.device()?.client ?? this._restService.device()?.distributorID;
    const clientType = this._restService.device()?.type;
    const appId = this._restService.device()?.appID;
    const isRecommended = this._isTechMode();
    if (!editedData?.dailyRoutes || !token || !clientId || !clientType || !appId) return null;
    return {
      id: editedData.id,
      token,
      client_id: clientId,
      client_type: clientType,
      app_id: appId,
      dailyRoutes: editedData.dailyRoutes,
      is_recommended: isRecommended,
    };
  });
  
  // Local properties
  private _warningDismissSub?: Subscription;

  // Http requests
  public syncEditedCustomRouteDataWithPythonBackendServiceSignal(body: EditedCustomRouteDataSyncBody): void {
    this._syncEditedCustomRouteDataWithPythonBackend(body).subscribe({
      next: (response: EditedCustomRouteDataSyncResponse) => {
        this._incrementEditActionCounter();

        // HRS-2658: un sync OK invalida el error del sync anterior.
        this._errorEditedCustomRouteData.set(null);
        this.setEditedCustomRouteData(response.route as CustomRoute);
        this._summaryEditedCustomRouteData.set(response.summary); // ? Render in snackbar (custom-route.component.ts)
        
        // check if there is some point with prop isValid false
        const hasInvalidPoints = response.route.dailyRoutes.some(day => day.points.some(point => !point.isValid));
        if (hasInvalidPoints) {
          const message = this._translationService.instant('Hay puntos fuera de horario');
          this._warningMessageEditedCustomRouteData.set(message);

          // dismiss warning after 4100ms
          this._warningDismissSub?.unsubscribe();
          this._warningDismissSub = timer(4100).subscribe(() => {
            this._warningMessageEditedCustomRouteData.set(null);
          });
        }
      },
      error: (error: any) => {
        // El observable envuelve el error en `new Error(..., { cause })`, así que
        // el HttpErrorResponse llega en `error.cause`, no en `error`. Antes se
        // hacía `error.error.message` a secas → "Cannot read properties of
        // undefined" en cualquier fallo del sync (p.ej. el 500 del backend).
        const httpError = error?.cause ?? error;
        const message = httpError?.error?.message
          ?? httpError?.message
          ?? this._translationService.instant('No se pudo actualizar la ruta. Inténtalo de nuevo.');
        this._errorEditedCustomRouteData.set(message); // ? Render in snackbar (custom-route.component.ts)
      }
    });
  }
   
  private _syncEditedCustomRouteDataWithPythonBackend(body: EditedCustomRouteDataSyncBody): Observable<EditedCustomRouteDataSyncResponse> {
    this._isLoadingEditedCustomRouteData.set(true);
    const lang = this._translationService.selectedLanguage();
    const url = `${environment.apiAssistantUrlBase}/route/sync-route?lang=${lang}`;
    return this._http.put<EditedCustomRouteDataSyncResponse>(url, body, { observe: 'response' })
      .pipe(
        map((httpResponse) => httpResponse.body),
        catchError(error => throwError(() => new Error('HTTP request failed', { cause: error }))),
        finalize(() => this._isLoadingEditedCustomRouteData.set(false))
    );
  }
  
  public restoreEditedCustomRouteDataWithPythonBackend(): void {
    this._restoreEditedCustomRouteDataWithPythonBackend().subscribe({
      next: (response: CustomRouteResponseCompleted) => {
        this._restoreEditedCustomRouteDataToOriginal(response);
        
        const message = this._translationService.instant('Ruta restaurada correctamente');
        this._summaryEditedCustomRouteData.set({
          actions: [], // doesnt care
          hasChanges: false, // doesnt care
          modifiedDays: [], // doesnt care
          message: message,
        }); // ? Render in snackbar (custom-route.component.ts)
      },
      error: (error: any) => {
        // El observable envuelve el error en `new Error(..., { cause })`, así que
        // el HttpErrorResponse llega en `error.cause`, no en `error`. Antes se
        // hacía `error.error.message` a secas → "Cannot read properties of
        // undefined" en cualquier fallo del sync (p.ej. el 500 del backend).
        const httpError = error?.cause ?? error;
        const message = httpError?.error?.message
          ?? httpError?.message
          ?? this._translationService.instant('No se pudo actualizar la ruta. Inténtalo de nuevo.');
        this._errorEditedCustomRouteData.set(message); // ? Render in snackbar (custom-route.component.ts)
      }
    });
  }
  
  private _restoreEditedCustomRouteDataWithPythonBackend(): Observable<CustomRouteResponseCompleted> {
    this._isLoadingRestoreEditedCustomRouteData.set(true);
    
    const url = `${environment.apiAssistantUrlBase}/api/restore-original-route`;
    const body = { 
      token: this._customRouteService.token(),
      client_id: this._restService.device()?.client ?? this._restService.device()?.distributorID,
      client_type: this._restService.device()?.type,
      app_id: this._restService.device()?.appID,
      is_recommended: this._isTechMode(),
    };
    if (!body.token || !body.client_id || !body.client_type || !body.app_id) return throwError(() => new Error('Route token or client id or client type or app id is required'));
    
    return this._http.put<CustomRouteResponseCompleted>(url, body)
      .pipe(
        map((response) => {
          this._isLoadingRestoreEditedCustomRouteData.set(false);
          return response;
        }),
        catchError(error => {
          this._isLoadingRestoreEditedCustomRouteData.set(false);
          return throwError(() => new Error('HTTP request failed', { cause: error }));
        })
    );
  }
  
  // Methods
  private _incrementEditActionCounter(): void {
    //if (!this._isEditRouteMode()) return;
    this._editActionCounter.update(count => count + 1);
    this._sessionStorageService.setItem('editActionCounter', this._editActionCounter());
  }

  public updateStartEndPoints(updates: StartEndPointUpdate[]): void {
    const source = this._editedCustomRouteData() ?? this._customRouteService.originalCustomRouteData();
    if (!source || updates.length === 0) return;

    const updatedRoute: CustomRoute = {
      ...source,
      dailyRoutes: source.dailyRoutes.map((day, i) => {
        const update = updates.find(u => u.dayIndex === i);
        if (!update) return day;

        return {
          ...day,
          ...(update.startPoint !== undefined ? { startPoint: update.startPoint } : {}),
          ...(update.endPoint !== undefined ? { endPoint: update.endPoint } : {}),
        };
      })
    };

    this.setEditedCustomRouteData(updatedRoute);
  }

  /**
   * Inserts a point at the given day index and insert index.
   * @param dayIndex
   * @param insertIndex
   * @param point
   * @returns
   */
  public insertPointAtIndex(dayIndex: number, insertIndex: number, point: Partial<Point>): void {
    const source = this._editedCustomRouteData() ?? this._customRouteService.originalCustomRouteData();
    if (!source) return;

    const updatedRoute: CustomRoute = {
      ...source,
      dailyRoutes: source.dailyRoutes.map((day, i) => {
        if (i !== dayIndex) return day;

        const updatedPoints = [...day.points];
        updatedPoints.splice(insertIndex, 0, point as Point);
        return { ...day, points: updatedPoints };
      })
    };

    this.setEditedCustomRouteData(updatedRoute);
    //this._incrementEditActionCounter();
    this._plannerDayStateService.setDayToShowIndex(dayIndex);
  }

  /**
   * Removes a point from editedCustomRouteData by day index and point ID.
   * Falls back to customRouteData if no edits exist yet.
   */
  removePointFromEditedRoute(dayIndex: number, pointId: number): void {
    const source = this._editedCustomRouteData() ?? this._customRouteService.originalCustomRouteData();
    if (!source) return;

    const dayPoints = source.dailyRoutes[dayIndex]?.points;
    if (!dayPoints?.some(p => p.id === pointId)) return;

    const updatedRoute: CustomRoute = {
      ...source,
      dailyRoutes: source.dailyRoutes.map((day, index) => {
        if (index !== dayIndex) return day;
        return { ...day, points: day.points.filter(p => p.id !== pointId) };
      })
    };

    this.setEditedCustomRouteData(updatedRoute);
    //this._incrementEditActionCounter();
    this._plannerDayStateService.setDayToShowIndex(dayIndex);
  }

  /**
   * Adds a point to editedCustomRouteData at the given day, sorted by startDate.
   * Falls back to originalCustomRouteData if no edits exist yet.
   */
  addPointToEditedRoute(dayIndex: number, point: Partial<Point>): void {
    const source = this._editedCustomRouteData() ?? this._customRouteService.originalCustomRouteData();
    if (!source) return;

    const updatedRoute: CustomRoute = {
      ...source,
      dailyRoutes: source.dailyRoutes.map((day, index) => {
        if (index !== dayIndex) return day;

        const updatedPoints = [...day.points, point as Point]
          .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

        return { ...day, points: updatedPoints };
      })
    };

    this.setEditedCustomRouteData(updatedRoute);
    //this._incrementEditActionCounter();
    this._plannerDayStateService.setDayToShowIndex(dayIndex);
  }

  /**
   * Moves a point from one position to another within editedCustomRouteData.
   * Supports same-day reorder and cross-day transfers.
   */
  movePointInEditedRoute(
    sourceDayIndex: number,
    sourcePointIndex: number,
    targetDayIndex: number,
    targetPointIndex: number,
    targetStartDate?: Date
  ): void {
    const source = this._editedCustomRouteData() ?? this._customRouteService.originalCustomRouteData();
    if (!source) return;

    const point = source.dailyRoutes[sourceDayIndex]?.points[sourcePointIndex];
    if (!point) return;

    const adjustedPoint = targetStartDate
      ? this._applyStartDate(point, targetStartDate)
      : point;

    const updatedRoute: CustomRoute = {
      ...source,
      dailyRoutes: source.dailyRoutes.map((day, index) => {
        if (sourceDayIndex === targetDayIndex) {
          if (index !== sourceDayIndex) return day;

          const points = [...day.points];
          points.splice(sourcePointIndex, 1);
          points.splice(targetPointIndex, 0, adjustedPoint);
          return { ...day, points: this._invalidateStalePolylines(day.points, points) };
        }

        if (index === sourceDayIndex) {
          const filtered = day.points.filter((_, i) => i !== sourcePointIndex);
          return { ...day, points: this._invalidateStalePolylines(day.points, filtered) };
        }
        if (index === targetDayIndex) {
          const points = [...day.points];
          const pointToInsert = targetStartDate
            ? adjustedPoint
            : this._adjustPointDatesForDay(point, day, points, targetPointIndex);
          points.splice(targetPointIndex, 0, pointToInsert);
          return { ...day, points: this._invalidateStalePolylines(day.points, points) };
        }
        return day;
      })
    };

    this.setEditedCustomRouteData(updatedRoute);
    //this._incrementEditActionCounter();
    this._plannerDayStateService.setDayToShowIndex(targetDayIndex);
  }

  /**
   * Swaps two points in editedCustomRouteData.
   * Supports same-day and cross-day swaps.
   */
  swapPointsInEditedRoute(
    dayIndexA: number,
    pointIndexA: number,
    dayIndexB: number,
    pointIndexB: number
  ): void {
    const source = this._editedCustomRouteData() ?? this._customRouteService.originalCustomRouteData();
    if (!source) return;

    const pointA = source.dailyRoutes[dayIndexA]?.points[pointIndexA];
    const pointB = source.dailyRoutes[dayIndexB]?.points[pointIndexB];
    if (!pointA || !pointB) return;

    const pointBAtPositionA = this._applyStartDate(pointB, new Date(pointA.startDate));
    const pointAAtPositionB = this._applyStartDate(pointA, new Date(pointB.startDate));

    const updatedRoute: CustomRoute = {
      ...source,
      dailyRoutes: source.dailyRoutes.map((day, index) => {
        if (dayIndexA === dayIndexB) {
          if (index !== dayIndexA) return day;
          const points = [...day.points];
          points[pointIndexA] = pointBAtPositionA;
          points[pointIndexB] = pointAAtPositionB;
          return { ...day, points: this._invalidateStalePolylines(day.points, points) };
        }

        if (index === dayIndexA) {
          const points = [...day.points];
          points[pointIndexA] = pointBAtPositionA;
          return { ...day, points: this._invalidateStalePolylines(day.points, points) };
        }
        if (index === dayIndexB) {
          const points = [...day.points];
          points[pointIndexB] = pointAAtPositionB;
          return { ...day, points: this._invalidateStalePolylines(day.points, points) };
        }
        return day;
      })
    };

    this.setEditedCustomRouteData(updatedRoute);
    //this._incrementEditActionCounter();
  }
  
  private _adjustPointDatesForDay(point: Point, day: DailyRoute, existingPoints: Point[], insertIndex: number): Point {
    const durationMs = new Date(point.endDate).getTime() - new Date(point.startDate).getTime();

    let newStartMs: number;

    if (insertIndex > 0 && existingPoints[insertIndex - 1]) {
      newStartMs = new Date(existingPoints[insertIndex - 1].endDate).getTime();
    } else if (existingPoints.length > 0) {
      newStartMs = new Date(existingPoints[0].startDate).getTime() - durationMs;
    } else {
      const dayDate = new Date(day.date);
      const origStart = new Date(point.startDate);
      dayDate.setHours(origStart.getHours(), origStart.getMinutes(), origStart.getSeconds(), origStart.getMilliseconds());
      newStartMs = dayDate.getTime();
    }

    return this._applyStartDate(point, new Date(newStartMs));
  }

  private _applyStartDate(point: Point, targetStartDate: Date): Point {
    const durationMs = new Date(point.endDate).getTime() - new Date(point.startDate).getTime();
    return {
      ...point,
      startDate: targetStartDate,
      endDate: new Date(targetStartDate.getTime() + durationMs),
    };
  }

  /**
   * Compares each point's predecessor in the updated array against the original.
   * Clears polylinesPath for any point whose predecessor changed so that
   * PolylinesService re-fetches the correct walking route.
   */
  private _invalidateStalePolylines(originalPoints: Point[], updatedPoints: Point[]): Point[] {
    const originalPredecessor = new Map<number, number | null>();
    originalPoints.forEach((p, i) => {
      originalPredecessor.set(p.id, i > 0 ? originalPoints[i - 1].id : null);
    });

    return updatedPoints.map((p, i) => {
      const newPredId = i > 0 ? updatedPoints[i - 1].id : null;
      const oldPredId = originalPredecessor.get(p.id);

      if (oldPredId === undefined || oldPredId !== newPredId) {
        return { ...p, polylinesPath: [] };
      }

      return p;
    });
  }

  public resetEditState(): void {
    this._editedCustomRouteData.set(null);
    this._editActionCounter.set(0);
  }

  private _restoreEditedCustomRouteDataToOriginal(response: CustomRouteResponseCompleted): void {
    const adaptedRouteData = customRouteAdapter(response.id, response.dailyRoutes, response.recommended_route);
    this._editedCustomRouteData.set(adaptedRouteData);
    this._customRouteService.setOriginalCustomRouteData(adaptedRouteData);
    
    this.setEditActionCounter(0);
  
    this._sessionStorageService.removeItem('isEditRouteMode');
    this._sessionStorageService.removeItem('editActionCounter');
  }
}
